import React from 'react';
import { Play, Clock, CheckCircle, Lock } from 'lucide-react';
import type { TrainingModule } from '../types';
import { cn } from '../utils/cn';

interface ModuleCardProps {
  module: TrainingModule;
  completed?: boolean;
  available?: boolean;
  onClick: () => void;
}

export function ModuleCard({ module, completed, available = true, onClick }: ModuleCardProps) {
  return (
    <div 
      className={cn(
        "group relative overflow-hidden rounded-lg bg-white shadow-sm transition-all",
        "hover:shadow-md hover:scale-[1.02] active:scale-[0.98]",
        !available && "opacity-75"
      )}
    >
      <div className="aspect-video w-full bg-gray-900 relative overflow-hidden">
        {module.thumbnail ? (
          <img
            src={module.thumbnail}
            alt={module.title}
            className={cn(
              "h-full w-full object-cover transition-transform duration-300",
              available && "group-hover:scale-105"
            )}
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-800">
            <Play className="h-12 w-12 text-gray-400" />
          </div>
        )}
        
        {completed && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="rounded-full bg-white/90 p-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </div>
        )}

        {!available && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="rounded-full bg-white/90 p-4">
              <Lock className="h-8 w-8 text-gray-400" />
            </div>
          </div>
        )}

        <div 
          className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent"
          style={{ backdropFilter: 'blur(4px)' }}
        >
          <div className="flex items-center gap-2 text-white text-sm">
            <Clock className="h-4 w-4" />
            <span>{Math.floor(module.duration / 60)}min</span>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="mb-4">
          <h3 className="font-semibold text-gray-900 text-lg line-clamp-1">
            {module.title}
          </h3>
          <p className="mt-1 text-sm text-gray-500 line-clamp-2">
            {module.description}
          </p>
        </div>

        <button
          onClick={onClick}
          disabled={!available}
          className={cn(
            "w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5",
            "text-sm font-medium text-white transition-all",
            "hover:opacity-90 active:scale-95",
            !available && "opacity-50 cursor-not-allowed"
          )}
          style={{ backgroundColor: '#7c3aed' }}
        >
          {!available ? (
            <>
              <Lock className="h-4 w-4" />
              Bloqueado
            </>
          ) : completed ? (
            <>
              <Play className="h-4 w-4" />
              Revisar
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />
              Iniciar
            </>
          )}
        </button>
      </div>
    </div>
  );
}